import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFloppyDisk,
  faGraduationCap,
  faPlus,
  faSpinner,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { hrToolsService } from '../../services/hrToolsService'
import {
  DEFAULT_CERT_TYPE_DEFS,
  MAX_CERT_TYPE_LABEL_LENGTH,
  MAX_CERT_TYPES,
  type CertTypeDef,
} from '../../utils/certLabels'

const HrCertTypesCard: React.FC = () => {
  const [draft, setDraft] = useState<CertTypeDef[]>([...DEFAULT_CERT_TYPE_DEFS])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const next = await hrToolsService.getCertTypes()
        if (!cancelled) setDraft(next.length ? next : [...DEFAULT_CERT_TYPE_DEFS])
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load certification types')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const patchAt = (index: number, label: string) => {
    setDraft((prev) => prev.map((item, i) => (i === index ? { ...item, label } : item)))
    setMessage(null)
  }

  const removeAt = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index))
    setMessage(null)
  }

  const addRow = () => {
    setDraft((prev) => (prev.length >= MAX_CERT_TYPES ? prev : [...prev, { key: '', label: '' }]))
    setMessage(null)
  }

  const onSave = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const next = await hrToolsService.saveCertTypes(draft)
      setDraft(next)
      setMessage('Saved.')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        <h2 className="card-title">
          <FontAwesomeIcon icon={faGraduationCap} />
          Certification types
        </h2>
        <p className="text-sm text-base-content/70">
          These names appear when HR assigns certifications and on Profile, Control Center, and
          Resources. Renaming a type keeps existing assignments. You cannot remove a type that is
          still assigned to someone.
        </p>

        {error ? <div className="alert alert-error mt-2"><span>{error}</span></div> : null}
        {message ? <div className="alert alert-success mt-2"><span>{message}</span></div> : null}

        {loading ? (
          <div className="flex items-center gap-2 py-8 text-base-content/70">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
            Loading certification types…
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {draft.length === 0 ? (
              <p className="text-sm text-base-content/60">Add at least one certification type.</p>
            ) : (
              <div className="space-y-2">
                {draft.map((row, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <input
                        className="input input-bordered w-full"
                        value={row.label}
                        maxLength={MAX_CERT_TYPE_LABEL_LENGTH}
                        onChange={(e) => patchAt(index, e.target.value)}
                        placeholder="Certification name"
                        aria-label={`Certification type ${index + 1}`}
                      />
                      {row.key ? (
                        <p className="mt-0.5 text-xs text-base-content/50">{row.key}</p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm btn-square text-error"
                      onClick={() => removeAt(index)}
                      aria-label="Remove"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn btn-outline"
                disabled={draft.length >= MAX_CERT_TYPES}
                onClick={addRow}
              >
                <FontAwesomeIcon icon={faPlus} />
                Add type
              </button>
              <button
                type="button"
                className="btn btn-primary gap-2"
                disabled={saving}
                onClick={() => void onSave()}
              >
                <FontAwesomeIcon
                  icon={saving ? faSpinner : faFloppyDisk}
                  className={saving ? 'animate-spin' : ''}
                />
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HrCertTypesCard
